import { useCallback, useMemo } from 'react';
import { miembroService } from '../services/miembroService';
import { ComboboxLazyOption } from '../components/ui/ComboboxLazy';
import { OwnerContext } from '../types/actividad';

export const useMiembroSearchJA = (ownerContext: OwnerContext | null) => {
  const searchCache = useMemo(() => new Map<string, ComboboxLazyOption[]>(), []);

  const handleSearch = useCallback(async (searchTerm: string): Promise<ComboboxLazyOption[]> => {
    if (!ownerContext || !searchTerm.trim()) return [];
    if (!['diocesis', 'provincia'].includes(ownerContext.tipo)) return [];

    const cacheKey = `${ownerContext.tipo}-${ownerContext.id}-${searchTerm.toLowerCase()}`;
    if (searchCache.has(cacheKey)) return searchCache.get(cacheKey)!;

    try {
      const results = ownerContext.tipo === 'diocesis'
        ? await miembroService.searchByDiocesis(ownerContext.id, searchTerm, 50)
        : await miembroService.searchByProvincia(ownerContext.id, searchTerm, 50);

      const options: ComboboxLazyOption[] = results.map(m => ({
        value: m.id,
        label: `${m.nombres} ${m.apellidos}`,
        subtitle: m.rut_pasaporte || '',
        data: m
      }));

      if (searchCache.size >= 100) {
        const firstKey = searchCache.keys().next().value;
        if (firstKey) searchCache.delete(firstKey);
      }
      searchCache.set(cacheKey, options);
      return options;
    } catch (error) {
      console.error('Error buscando miembros:', error);
      return [];
    }
  }, [ownerContext, searchCache]);

  const clearCache = useCallback(() => { searchCache.clear(); }, [searchCache]);

  return {
    handleSearch,
    clearCache,
    canSearch: !!ownerContext && ownerContext.tipo !== 'global'
  };
};
